import { useState, Suspense } from "react";
import { useTranslation } from "react-i18next";

import type { ActiveWidget } from "@/pages/EventPage/hooks/useEventWidgets";

import { useGetProfile } from "@/api/bale";
import { Card, CardSubtitleHeader } from "@/components/Card";
import { uuidVersion } from "@/fp/uuidVersion";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";

import { EditingPollCard } from "./EditingPollCard";
import { StaticPollCard } from "./StaticPollCard";

export type PollWidget = Extract<ActiveWidget, { widget_type: "poll" }>;

export function PollCard({ widget }: { widget: PollWidget }) {
  const { t } = useTranslation();

  return (
    <Suspense
      fallback={
        <Card>
          <CardSubtitleHeader>{t("event-poll")}</CardSubtitleHeader>
        </Card>
      }
    >
      <EditablePollCardContent widget={widget} />
    </Suspense>
  );
}

function EditablePollCardContent({ widget }: { widget: PollWidget }) {
  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });

  const isTransient = uuidVersion(widget.id) === 4;
  const [isEditing, setIsEditing] = useState(isTransient);
  const isHost = me.isSuccess && event.data.event.account_id === me.data.id;

  if (isEditing) {
    return (
      <EditingPollCard
        widget={widget}
        onSave={() => setIsEditing(false)}
        onCancel={isTransient ? undefined : () => setIsEditing(false)}
      />
    );
  }

  return <StaticPollCard widget={widget} onEdit={isHost ? () => setIsEditing(true) : undefined} />;
}
